import _sample from 'lodash/sample';
import _debounce from 'lodash/debounce';
import {tabletBreakpoint, desktopBreakpoint} from '../../../lib/styleVariables';

const FPS = 30;
const STEP_PER_FRAME = 0.02;
const SCROLL_STOP_TIMEOUT = 700;
const RESIZE_TIMEOUT = 700;

export const canvasHeight = {
  desktop: 2000,
  tablet: 1500,
  mobile: 750
};

const COLORS = {
  pink: 340,
  blue: 195,
  green: 155,
  orange: 49
};

const colorPairing: Array<[number, number]> = [
  [COLORS.pink, COLORS.green],
  [COLORS.orange, COLORS.blue],
  [COLORS.green, COLORS.orange],
  [COLORS.blue, COLORS.pink]
];

function sampleColorPairing(excludeItems: Array<[number, number]> = []) {
  return _sample(colorPairing.filter(item => excludeItems.indexOf(item) === -1)) as [number, number];
}

function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('safari') !== -1)
    if (ua.indexOf('chrome') === -1) {
      return true;
    }

  return false;
}

class BubbleDrawer {
  private readonly canvasCtx: CanvasRenderingContext2D | null;
  private readonly canvas: HTMLCanvasElement;
  private readonly rotate: number = -15 * (Math.PI / 180);

  private globalWidth?: number;
  private globalHeight?: number;

  private step = 9;
  private lastDrawnAt = Date.now();

  private readonly randomColors = sampleColorPairing();
  private readonly newColors = sampleColorPairing([this.randomColors]);
  private nextColors = sampleColorPairing([this.randomColors, this.newColors]);

  private lastLeftHue = this.randomColors[0];
  private currentLeftHue = this.lastLeftHue;
  private nextLeftHue = this.newColors[0];
  private isDoneLeft = false;

  private lastRightHue = this.randomColors[1];
  private currentRightHue = this.lastRightHue;
  private nextRightHue = this.newColors[1];
  private isDoneRight = false;

  private isChanging = false;

  private isDoneCalled = false;

  private isStarted = false;
  private isScrolling = false;
  private timer: any;
  private isRemoved = false;

  constructor(canvas: HTMLCanvasElement, rotate?: number) {
    this.canvas = canvas;
    this.canvasCtx = canvas.getContext('2d');
    if (typeof rotate === 'number')
      this.rotate = rotate;

    this.rescale();
    this.draw();
  }

  start = () => {
    this.isStarted = true;
  };

  stop = () => {
    this.isStarted = false;
  };

  init = () => {
    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('scroll', this.scrollListener);
    requestAnimationFrame(this.update);
  };

  remove = () => {
    this.isRemoved = true;
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('scroll', this.scrollListener);
  };

  scrollListener = () => {
    clearTimeout(this.timer);
    this.isScrolling = true;

    this.timer = setTimeout(() => {
      this.isScrolling = false;
    }, SCROLL_STOP_TIMEOUT);
  };

  resizeListener = _debounce(() => this.rescale(), RESIZE_TIMEOUT);

    private applyFilter = () => {
      if (this.globalHeight && this.canvasCtx) {
        const blur = Math.max(60, this.globalHeight / 100);
        this.canvasCtx.filter = `blur(${blur}px)`;
        if (isSafari())
        // Safari requires css filtering
          this.canvas.style.webkitFilter = `blur(${blur}px)`;
      }
    }

  private update = () => {
    if (this.isRemoved)
      return;

    if (this.isStarted && !this.isScrolling && Date.now() - this.lastDrawnAt > 1000 / FPS) {
      this.draw();
      this.lastDrawnAt = Date.now();
    }

    requestAnimationFrame(this.update);
  };

  private draw = () => {
    if (!this.canvasCtx || !this.globalHeight)
      return;
    this.step -= STEP_PER_FRAME;

    this.changeColor();

    this.canvasCtx.save();
    this.canvasCtx.globalCompositeOperation = 'screen';
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasCtx.translate(this.canvas.width / 2, this.canvas.height / 2);
    this.canvasCtx.scale(0.7, 0.7);
    this.canvasCtx.rotate(this.rotate);

    this.applyFilter();

    this.drawArc('left');
    this.drawArc('right');

    this.canvasCtx.restore();
  };

  private drawArc = (position: 'left' | 'right') => {
    if (!this.canvasCtx || !this.globalHeight || !this.globalWidth)
      return;

    const aligner = position === 'left' ? -1 : 1;
    const x = Math.abs(Math.sin(this.step * 0.5) * this.globalWidth * 0.8) * aligner;
    const y = Math.abs(Math.sin(this.step * 0.0005) * this.globalHeight * 0.8) * aligner;
    const radius = Math.min(this.globalWidth, this.globalHeight) / (position === 'left' ? 2 : 1.7);

    this.canvasCtx.beginPath();

    this.canvasCtx.arc(x, y, radius, 0, Math.PI * 2, true);

    this.canvasCtx.fillStyle = `hsla(${
      position === 'left' ? this.currentLeftHue : this.currentRightHue
    }, 100%, 50%, 0.4)`;

    this.canvasCtx.fill();

    this.canvasCtx.closePath();
  };

  private rescale = () => {
    this.globalWidth = window.innerWidth;
    this.globalHeight = window.innerHeight;

    this.canvas.setAttribute('width', `${this.globalWidth}`);

    if (this.globalWidth < tabletBreakpoint - 1)
      this.canvas.setAttribute('height', `${canvasHeight.mobile}`);
    else if (this.globalWidth < desktopBreakpoint - 1)
      this.canvas.setAttribute('height', `${canvasHeight.tablet}`);
    else
      this.canvas.setAttribute('height', `${canvasHeight.desktop}`);
  };

  private changeColor = () => {
    this.isChanging =
      Math.round(this.lastLeftHue) !== Math.round(this.nextLeftHue) ||
      Math.round(this.lastRightHue) !== Math.round(this.nextRightHue);

    if (this.isChanging) {
      this.lastLeftHue = this.currentLeftHue;
      this.lastRightHue = this.currentRightHue;
    } else {
      this.nextColors = sampleColorPairing([this.nextColors]);

      const done = () =>
        setTimeout(() => {
          this.isDoneLeft = false;
          [this.nextLeftHue, this.nextRightHue] = this.nextColors;

          setTimeout(() => {
            this.isDoneCalled = false;
            this.isDoneRight = false;
          }, 3000);
        }, 5000);

      if (!this.isDoneCalled) {
        done();
        this.isDoneCalled = true;
      }
    }

    if (Math.round(this.lastLeftHue) === Math.round(this.nextLeftHue)) {
      this.isDoneLeft = true;
      this.lastLeftHue = this.currentLeftHue;
    }

    if (Math.round(this.lastRightHue) === Math.round(this.nextRightHue)) {
      this.isDoneRight = true;
      this.lastRightHue = this.currentRightHue;
    }

    const slowHueChanger = 0.5;

    if (!this.isDoneLeft && this.isChanging)
      this.currentLeftHue +=
        slowHueChanger * (Math.round(this.currentLeftHue) > Math.round(this.nextLeftHue) ? -1 : 1);

    if (!this.isDoneRight && this.isChanging)
      this.currentRightHue +=
        slowHueChanger * (Math.round(this.currentRightHue) > Math.round(this.nextRightHue) ? -1 : 1);
  };
}

export default BubbleDrawer;
